/**
 * SicBo game-domain types.
 * Ported from packages/amgsicbo_v2/lib/core/constants/game_constants.dart
 *
 * SicBo has 54 bet item IDs (1..54). IDs map to:
 *   1=Small, 2=Big, 3=Odd, 4=Even, 5..18=Total 4..17,
 *   19..24=Single 1..6, 25..30=Double 1..6, 31..36=Triple 1..6,
 *   37=Any Triple, 38..52=Combos (15 pairs), 53/54=spare slots.
 */

export type GamePhase =
  | 'idle'
  | 'initializing'
  | 'modeSelection'
  | 'betting'
  | 'bettingClosed'
  | 'waitingResult'
  | 'diceRoll'
  | 'showResult'
  | 'result'
  | 'newRound'
  | 'error';

/** Discriminated bet type — `id` matches the server's betItemId. */
export interface BetType {
  id: number;
  name: string;
}

// -------------------------------------------------------------------------
// Bet table — id → display name. Mirrors GameConstants.betTypes
// (packages/amgsicbo_v2/lib/core/constants/game_constants.dart:17-72).
// -------------------------------------------------------------------------

export const BET_TYPES: ReadonlyArray<BetType> = [
  { id: 1, name: 'Small' },
  { id: 2, name: 'Big' },
  { id: 3, name: 'Odd' },
  { id: 4, name: 'Even' },
  { id: 5, name: 'Total 4' },
  { id: 6, name: 'Total 5' },
  { id: 7, name: 'Total 6' },
  { id: 8, name: 'Total 7' },
  { id: 9, name: 'Total 8' },
  { id: 10, name: 'Total 9' },
  { id: 11, name: 'Total 10' },
  { id: 12, name: 'Total 11' },
  { id: 13, name: 'Total 12' },
  { id: 14, name: 'Total 13' },
  { id: 15, name: 'Total 14' },
  { id: 16, name: 'Total 15' },
  { id: 17, name: 'Total 16' },
  { id: 18, name: 'Total 17' },
  { id: 19, name: 'Single 1' },
  { id: 20, name: 'Single 2' },
  { id: 21, name: 'Single 3' },
  { id: 22, name: 'Single 4' },
  { id: 23, name: 'Single 5' },
  { id: 24, name: 'Single 6' },
  { id: 25, name: 'Double 1' },
  { id: 26, name: 'Double 2' },
  { id: 27, name: 'Double 3' },
  { id: 28, name: 'Double 4' },
  { id: 29, name: 'Double 5' },
  { id: 30, name: 'Double 6' },
  { id: 31, name: 'Triple 1' },
  { id: 32, name: 'Triple 2' },
  { id: 33, name: 'Triple 3' },
  { id: 34, name: 'Triple 4' },
  { id: 35, name: 'Triple 5' },
  { id: 36, name: 'Triple 6' },
  { id: 37, name: 'Any Triple' },
  { id: 38, name: 'Combo 1-2' },
  { id: 39, name: 'Combo 1-3' },
  { id: 40, name: 'Combo 1-4' },
  { id: 41, name: 'Combo 1-5' },
  { id: 42, name: 'Combo 1-6' },
  { id: 43, name: 'Combo 2-3' },
  { id: 44, name: 'Combo 2-4' },
  { id: 45, name: 'Combo 2-5' },
  { id: 46, name: 'Combo 2-6' },
  { id: 47, name: 'Combo 3-4' },
  { id: 48, name: 'Combo 3-5' },
  { id: 49, name: 'Combo 3-6' },
  { id: 50, name: 'Combo 4-5' },
  { id: 51, name: 'Combo 4-6' },
  { id: 52, name: 'Combo 5-6' },
  { id: 53, name: 'Specific Double' },
  { id: 54, name: 'Specific Triple' },
];

const BET_TYPE_BY_ID: Map<number, BetType> = new Map(
  BET_TYPES.map((b) => [b.id, b]),
);

export function betTypeById(id: number): BetType | null {
  return BET_TYPE_BY_ID.get(id) ?? null;
}

// -------------------------------------------------------------------------
// Payout table — multiplier per bet id. Mirrors GameConstants payouts
// (game_constants.dart:120-132).
// -------------------------------------------------------------------------

const TOTAL_PAYOUTS: Record<number, number> = {
  4: 60,
  5: 30,
  6: 18,
  7: 12,
  8: 8,
  9: 6,
  10: 6,
  11: 6,
  12: 6,
  13: 8,
  14: 12,
  15: 18,
  16: 30,
  17: 60,
};

export function payoutFor(id: number): number {
  if (id === 1 || id === 2) return 1; // Small / Big
  if (id === 3 || id === 4) return 1; // Odd / Even
  if (id >= 5 && id <= 18) return TOTAL_PAYOUTS[id - 1] ?? 0;
  if (id >= 19 && id <= 24) return 1; // Single (base; actual pays 1/2/3 per occurrences)
  if (id >= 25 && id <= 30) return 10; // Double
  if (id >= 31 && id <= 36) return 180; // Triple
  if (id === 37) return 30; // Any Triple
  if (id >= 38 && id <= 52) return 6; // Combo
  return 0;
}

// -------------------------------------------------------------------------
// Dice result category — used in history + setHighlight.
// -------------------------------------------------------------------------

export type DiceCategory = 'small' | 'big' | 'triple';

export interface GameConfig {
  minBet: number;
  maxBet: number;
  chipValues: readonly number[];
}

export interface GameRound {
  roundId: string;
  nextRoundId: string;
  dice1: number;
  dice2: number;
  dice3: number;
  total: number;
  isTriple: boolean;
  category: DiceCategory;
  totalWinLose: number;
  totalBet: number;
  positiveWinAmount: number;
  hasPositiveWin: boolean;
}
